import React, { useState } from 'react';
import { MapPin, Search, X, Calendar, Package, Eye } from 'lucide-react';

function UserDeliveriesList({ deliveries, onViewDetails }) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter deliveries based on search
  const filteredDeliveries = deliveries.filter(delivery => {
    const searchLower = searchTerm.toLowerCase();
    return (
      delivery.id?.toString().includes(searchLower) ||
      delivery.recipient_name?.toLowerCase().includes(searchLower) ||
      delivery.recipient_phone?.includes(searchLower) ||
      delivery.district?.toLowerCase().includes(searchLower) ||
      delivery.status?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pendiente' },
      'encamino': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En Camino' },
      'entregado': { bg: 'bg-green-100', text: 'text-green-700', label: 'Entregado' },
      'rechazado': { bg: 'bg-red-100', text: 'text-red-700', label: 'Rechazado' },
      'postergado': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Postergado' },
      'cancelado': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Cancelado' },
    };
    
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    return (
      <span className={`text-xs px-2 py-1 rounded-full font-medium font-montserrat ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#B1336E]" />
          <h4 className="font-semibold text-slate-800 font-montserrat">
            Entregas Activas
          </h4>
          <span className="text-xs bg-[#B1336E]/10 text-[#B1336E] px-2 py-0.5 rounded-full font-medium font-montserrat">
            {deliveries.length}
          </span>
        </div>
      </div>

      {/* Search Bar */}
      {deliveries.length > 3 && (
        <div className="mb-3 relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre, teléfono, ID..."
            className="w-full pl-9 pr-8 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E] focus:border-transparent font-montserrat"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      )}

      {/* Deliveries List */}
      {filteredDeliveries.length === 0 ? (
        <div className="text-center py-8">
          <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500 font-montserrat">
            {searchTerm ? 'No se encontraron entregas' : 'Sin entregas activas'}
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
          {filteredDeliveries.map((delivery) => (
            <div 
              key={delivery.id} 
              className="bg-white rounded-lg p-3 border border-slate-200 hover:border-[#B1336E] transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-xs font-bold text-[#B1336E] font-montserrat">
                    #{delivery.id}
                  </span>
                  {getStatusBadge(delivery.status)}
                </div>
                {onViewDetails && (
                  <button
                    onClick={() => onViewDetails(delivery.id, 'delivery')}
                    className="p-1.5 hover:bg-[#B1336E]/10 rounded-lg transition-colors flex-shrink-0"
                    title="Ver detalles"
                  >
                    <Eye className="w-4 h-4 text-[#B1336E]" />
                  </button>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-900 truncate font-montserrat">
                  {delivery.recipient_name || 'Sin nombre'}
                </p>
                
                {delivery.recipient_phone && (
                  <p className="text-xs text-slate-600 font-montserrat">
                    📱 {delivery.recipient_phone}
                  </p>
                )}

                {delivery.district && (
                  <p className="text-xs text-slate-600 font-montserrat">
                    📍 {delivery.district}
                  </p>
                )}

                {delivery.delivery_date && (
                  <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                    <Calendar className="w-3 h-3" />
                    <span className="font-montserrat">{formatDate(delivery.delivery_date)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UserDeliveriesList;